/* =====================================================================
   Pilates Quiz Funnel — engine ported from "Cozy Pilates Quiz" HTML.
   Content (questions, answers, copy, prices, photos) comes from the
   Shopify sections and blocks; the flow and markup match the original.
   ===================================================================== */
(function () {
  "use strict";

  /* ---------- Illustration set (120 x 90 line drawings, follow theme colors) ---------- */
  const _S = inner => `<svg viewBox="0 0 120 90" aria-hidden="true">${inner}</svg>`;
  const _L = (d, c = "var(--ink)", w = 5) => `<path d="${d}" fill="none" stroke="${c}" stroke-width="${w}" stroke-linecap="round" stroke-linejoin="round"/>`;
  const _H = (x, y, r = 6, c = "var(--ink)") => `<circle cx="${x}" cy="${y}" r="${r}" fill="${c}"/>`;
  const _F = (d, c = "var(--accent)", o = 1) => `<path d="${d}" fill="${c}" opacity="${o}"/>`;
  const _G = _L("M18 82 H102", "var(--accent)", 4);

  function _body(zone) {
    const hi = "var(--accent)", lo = "var(--muted)";
    const z = k => zone === k || zone === "full";
    return _S(`
      ${_H(60, 12, 8, z("posture") ? hi : lo)}
      ${_F("M47 24 h26 l-3 32 h-20z", z("core") || z("posture") ? "var(--soft)" : "var(--line)")}
      ${z("core") ? `<ellipse cx="60" cy="44" rx="9" ry="9" fill="${hi}"/>` : ""}
      ${z("posture") ? _L("M60 24 V56", hi, 5) : ""}
      ${_L("M48 27 L38 54", z("arms") ? hi : lo, 6)}${_L("M72 27 L82 54", z("arms") ? hi : lo, 6)}
      ${z("arms") ? _L("M48 26 H72", hi, 6) : ""}
      ${z("glutes") ? `<ellipse cx="60" cy="58" rx="13" ry="6" fill="${hi}"/>` : ""}
      ${_L("M54 58 L52 86", z("glutes") ? hi : lo, 7)}${_L("M66 58 L68 86", z("glutes") ? hi : lo, 7)}
    `);
  }

  function _fold(y) {
    return _S(`${_H(48, 14, 7, "var(--muted)")}${_L("M48 22 V52 M48 28 L40 46 M48 28 L56 46 M44 52 L42 84 M52 52 L54 84", "var(--muted)", 6)}
      ${_L(`M30 ${y} H90`, "var(--accent)", 4)}<path d="M100 ${y} l-10 -6 v12z" fill="var(--accent)"/>${_H(30, y, 5, "var(--accent)")}`);
  }

  const ICONS = {
    /* activity level */
    sitting: _S(`${_L("M34 28 V82 M34 56 H64 M64 56 V82")}${_H(48, 20)}${_L("M48 28 V54 H72 V80 M48 34 L64 44", "var(--accent)", 6)}${_G}`),
    walking: _S(`${_H(60, 14)}${_L("M60 22 L58 50 L48 80 M58 50 L70 80 M59 30 L48 44 M59 30 L72 42", "var(--accent)", 6)}${_G}`),
    stretching: _S(`${_H(60, 20)}${_L("M60 28 V54 L50 80 M60 54 L70 80 M56 32 L42 8 M64 32 L78 8", "var(--accent)", 6)}${_G}`),
    running: _S(`${_H(66, 14)}${_L("M64 22 L56 48 L40 58 L34 72 M56 48 L68 64 L80 68 M61 30 L46 40 M61 30 L78 34", "var(--accent)", 6)}${_G}`),
    lying: _S(`${_H(26, 62)}${_L("M34 64 H70 L84 50 L96 66", "var(--accent)", 6)}${_L("M18 72 H102", "var(--accent)", 4)}`),

    /* time of day */
    sunrise: _S(`${_F("M36 66 a24 24 0 0 1 48 0z")}${_L("M18 66 H102 M60 24 V32 M32 36 L38 42 M88 36 L82 42 M30 56 H22 M90 56 H98")}`),
    midday: _S(`${_H(60, 44, 16, "var(--accent)")}${_L("M60 12 V20 M60 68 V76 M28 44 H36 M84 44 H92 M38 22 L43 27 M82 22 L77 27 M38 66 L43 61 M82 66 L77 61")}`),
    lamp: _S(`${_F("M44 18 h32 l10 24 h-52z")}${_L("M60 42 V74 M44 78 H76")}`),
    moon: _S(`${_F("M70 14 a30 30 0 1 0 18 52 a24 24 0 1 1 -18 -52z")}${_H(34, 22, 3)}${_H(26, 46, 2)}${_H(92, 20, 2.5)}`),

    /* places */
    sofa: _S(`${_F("M30 44 h60 v14 h-60z", "var(--accent)", .55)}${_L("M22 46 V72 H98 V46 M30 46 V30 H90 V46 M22 58 H98 M28 72 V80 M92 72 V80")}`),
    bed: _S(`${_F("M44 42 h54 v14 h-54z")}${_L("M18 76 V28 M18 56 H102 V76 M22 44 h18 v12")}`),
    desk: _S(`${_F("M44 26 h32 v20 h-32z")}${_L("M38 48 H82 M18 50 H102 M28 50 V80 M92 50 V80")}`),
    suitcase: _S(`<rect x="30" y="30" width="60" height="46" rx="7" fill="var(--accent)"/>${_L("M48 30 V22 H72 V30")}${_L("M46 30 V76 M74 30 V76", "var(--bg)", 4)}${_H(40, 80, 3)}${_H(80, 80, 3)}`),
    mat: _S(`${_L("M16 70 H86", "var(--accent)", 8)}<circle cx="94" cy="62" r="11" fill="none" stroke="var(--accent)" stroke-width="7"/>`),
    candle: _S(`${_F("M50 40 h20 v40 h-20z")}${_F("M60 14 q10 12 0 22 q-10 -10 0 -22z", "var(--ink)")}${_L("M60 34 V40", "var(--ink)", 3)}`),
    tv: _S(`${_L("M24 18 H96 V64 H24z M48 78 H72 M60 64 V78")}${_F("M54 30 L72 41 L54 52z")}`),
    house: _S(`${_L("M24 48 L60 18 L96 48 M32 42 V80 H88 V42")}${_F("M52 56 h16 v24 h-16z")}`),
    car: _S(`${_F("M20 58 h80 v14 h-80z")}${_L("M30 58 L40 38 H78 L90 58")}${_H(38, 74, 8)}${_H(82, 74, 8)}`),
    standing: _S(`${_H(60, 14)}${_L("M60 22 V52 L54 82 M60 52 L66 82 M60 30 L48 48 M60 30 L72 48", "var(--accent)", 6)}${_G}`),
    laptop: _S(`${_F("M34 22 h52 v34 h-52z")}${_L("M24 64 H96 L90 72 H30z")}`),
    monitor: _S(`${_L("M22 16 H98 V60 H22z M60 60 V74 M44 76 H76")}${_F("M30 24 h60 v28 h-60z", "var(--accent)", .5)}`),
    phone: _S(`<rect x="44" y="12" width="32" height="66" rx="7" fill="none" stroke="var(--ink)" stroke-width="5"/>${_F("M50 22 h20 v44 h-20z")}`),

    /* feelings / goals */
    strong: _S(`${_H(60, 18)}${_L("M60 26 V56 M60 32 L44 36 L40 20 M60 32 L76 36 L80 20 M60 56 L50 82 M60 56 L70 82", "var(--accent)", 6)}`),
    firm: _S(`<circle cx="60" cy="45" r="30" fill="none" stroke="var(--line)" stroke-width="6"/><circle cx="60" cy="45" r="18" fill="none" stroke="var(--accent)" stroke-width="6"/>${_H(60, 45, 7)}`),
    energy: _S(`${_F("M66 8 L36 50 H58 L50 82 L84 36 H62z")}`),
    flexible: _S(`${_H(56, 14)}${_L("M56 22 Q60 38 58 54 L48 82 M58 54 L68 82 M57 28 Q70 14 84 18 M57 30 L44 44", "var(--accent)", 6)}${_G}`),
    calm: _S(`${_L("M18 40 q14 -14 28 0 t28 0 t28 0", "var(--accent)", 6)}${_L("M18 58 q14 -14 28 0 t28 0 t28 0", "var(--accent)", 6)}`),
    confident: _S(`${_F("M60 12 L69 34 L94 36 L75 52 L81 76 L60 63 L39 76 L45 52 L26 36 L51 34z")}`),

    /* lazy vibes */
    pajamas: _S(`${_F("M40 16 L24 30 L32 42 L40 36 V80 H80 V36 L88 42 L96 30 L80 16 Q60 28 40 16z")}${_H(60, 50, 2.5, "var(--bg)")}${_H(60, 62, 2.5, "var(--bg)")}`),
    timer: _S(`<circle cx="60" cy="48" r="28" fill="none" stroke="var(--ink)" stroke-width="5"/>${_F("M60 48 V24 A24 24 0 0 1 82 54z")}${_L("M52 12 H68 M60 12 V20")}`),
    slow: _S(`${_L("M18 70 q14 -30 28 0 t28 0 t28 0", "var(--accent)", 6)}`),

    /* holiday season */
    hosting: _S(`<circle cx="60" cy="48" r="24" fill="var(--accent)"/><circle cx="60" cy="48" r="14" fill="var(--soft)"/>${_L("M24 26 V70 M98 26 V70 M92 26 V44 Q98 44 98 38")}`),
    travel: _S(`<rect x="34" y="30" width="52" height="44" rx="7" fill="var(--accent)"/>${_L("M50 30 V22 H70 V30")}${_L("M48 30 V74 M72 30 V74", "var(--bg)", 4)}`),
    work: _S(`${_F("M34 22 h52 v34 h-52z")}${_L("M24 64 H96 L90 72 H30z")}`),
    quiet: _S(`${_F("M38 38 h36 v36 a8 8 0 0 1 -8 8 h-20 a8 8 0 0 1 -8 -8z")}${_L("M74 46 h6 a8 8 0 0 1 0 16 h-6 M48 16 q6 8 0 16 M62 16 q6 8 0 16")}`),
    party: _S(`${_F("M30 80 L50 30 L80 60z")}${_H(78, 22, 4)}${_H(92, 40, 3)}${_H(64, 16, 3)}${_L("M84 14 l4 4 M96 26 l4 -2")}`),
    photos: _S(`${_L("M22 28 H98 V76 H22z M44 28 L50 18 H70 L76 28")}<circle cx="60" cy="52" r="14" fill="var(--accent)"/>`),
    family: _S(`${_H(40, 32, 9, "var(--accent)")}${_H(80, 32, 9, "var(--accent)")}${_H(60, 48, 7)}${_F("M22 80 a18 20 0 0 1 36 0z")}${_F("M62 80 a18 20 0 0 1 36 0z")}${_F("M46 82 a14 14 0 0 1 28 0z", "var(--ink)")}`),
    none: _S(`<circle cx="60" cy="46" r="26" fill="none" stroke="var(--line)" stroke-width="6"/>${_L("M48 46 H72", "var(--muted)", 6)}`),

    /* forward fold check */
    fold_easy: _fold(82),
    fold_close: _fold(70),
    fold_far: _fold(58),

    /* body focus */
    body_core: _body("core"), body_glutes: _body("glutes"), body_arms: _body("arms"), body_posture: _body("posture"), body_full: _body("full")
  };

  /* Intro hero illustration (used only when the age opener is turned off) */
  const FIG = (extra) => `<svg viewBox="0 0 320 180" role="img">
    ${extra || ""}
    <rect x="40" y="146" width="240" height="9" rx="4.5" fill="var(--accent)" opacity=".35"/>
    <g fill="none" stroke="var(--ink)" stroke-width="8" stroke-linecap="round" stroke-linejoin="round">
      <path d="M92 140 L166 104 L206 96 L222 142"/>
      <path d="M96 142 L138 143"/>
    </g>
    <circle cx="74" cy="134" r="12" fill="var(--ink)"/>
  </svg>`;
  const HERO_ART = FIG(`<circle cx="262" cy="42" r="20" fill="var(--accent)" opacity=".5"/><circle cx="272" cy="36" r="18" fill="var(--soft)"/>
    <rect x="34" y="92" width="10" height="34" rx="3" fill="var(--accent)" opacity=".7"/><path d="M39 80 q6 6 0 12 q-6 -6 0 -12z" fill="var(--accent)"/>`);

  /* ---------- Helpers ---------- */
  const esc = s => String(s).replace(/[&<>"]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
  const blank = v => (v === undefined || v === null || String(v).trim() === "") ? undefined : v;
  const reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const isDesign = !!(window.Shopify && window.Shopify.designMode);
  const html = document.documentElement;
  function toTop() { window.scrollTo({ top: 0, behavior: reduceMotion ? "auto" : "smooth" }); }
  const splitList = s => String(s || "").split(/\r?\n/).map(x => x.trim()).filter(Boolean);
  const splitCsv = s => String(s || "").split(",").map(x => x.trim()).filter(Boolean);

  function fmtDate(d) { return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }); }

  /* ---------- Checkout + Quiz Lab (same contract as the other PG quizzes) ---------- */
  /* Mirrors assets/pg-mat-checkout-core-*.js: same subscription, same validation. */
  const ANNUAL_OFFER = Object.freeze({
    endpoint: "https://a56ba5-6f.myshopify.com/api/2026-10/graphql.json",
    variantId: "gid://shopify/ProductVariant/67709186343157",
    sellingPlanId: "gid://shopify/SellingPlan/10461085941",
    amount: 14.99,
    currency: "USD"
  });
  const ANNUAL_CART_MUTATION = `mutation QuizAnnualCart($input: CartInput!) {
  cartCreate(input: $input) {
    cart {
      checkoutUrl attributes { key value }
      cost { totalAmount { amount currencyCode } }
      lines(first: 2) { nodes {
        quantity merchandise { ... on ProductVariant { id } }
        sellingPlanAllocation { sellingPlan { id name } }
      } }
    }
    userErrors { field message code }
  }
}`;
  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

  function verifiedAnnualCheckout(response, input) {
    const result = response && response.data && response.data.cartCreate;
    if ((response && response.errors && response.errors.length) || (result && result.userErrors && result.userErrors.length) || !result || !result.cart) {
      throw new Error("Checkout is unavailable. Please try again.");
    }
    const cart = result.cart, lines = (cart.lines && cart.lines.nodes) || [];
    const line = lines[0] || {};
    if (lines.length !== 1 || line.quantity !== 1
      || !line.merchandise || line.merchandise.id !== ANNUAL_OFFER.variantId
      || !line.sellingPlanAllocation || line.sellingPlanAllocation.sellingPlan.id !== ANNUAL_OFFER.sellingPlanId
      || Number(cart.cost && cart.cost.totalAmount && cart.cost.totalAmount.amount) !== ANNUAL_OFFER.amount
      || (cart.cost && cart.cost.totalAmount && cart.cost.totalAmount.currencyCode) !== ANNUAL_OFFER.currency) {
      throw new Error("The checkout price or subscription has changed. Please try again later.");
    }
    const attrs = new Map((cart.attributes || []).map(a => [a.key, a.value]));
    if (input.attributes.some(a => attrs.get(a.key) !== a.value)) {
      throw new Error("Your quiz could not be linked to checkout. Please try again.");
    }
    let url;
    try { url = new URL(cart.checkoutUrl); } catch (_) { throw new Error("Checkout is unavailable. Please try again."); }
    if (url.protocol !== "https:" || url.hostname !== "pilatesflo.com" || url.username || url.password || url.port
      || !(/^\/cart\/c\//.test(url.pathname) || /^\/checkouts\//.test(url.pathname))) {
      throw new Error("Checkout is unavailable. Please try again.");
    }
    return url.href;
  }

  /* Same as pgTrybeCheckoutUrl in the other quiz runtimes: carry ?trybe= to checkout. */
  function trybeCheckoutUrl(destination) {
    try {
      const code = new URLSearchParams(window.location.search).get("trybe");
      if (!code || code.length > 2048 || /[\u0000-\u001f\u007f]/.test(code)) return destination;
      const url = new URL(destination);
      if (url.protocol !== "https:" || url.hostname !== "pilatesflo.com" || url.port || url.username || url.password) return destination;
      if (url.searchParams.has("trybe")) return destination;
      url.searchParams.set("trybe", code);
      return url.href;
    } catch (_) { return destination; }
  }

  /* Replaces [tokens] in merchant-editable copy */
  function fill(str, ctx) {
    return String(str || "").replace(/\[([a-z_]+)\]/g, (m, k) => (ctx && ctx[k] !== undefined ? ctx[k] : m));
  }

  /* ---------- Read steps from Shopify blocks ---------- */
  function readSteps(root) {
    return Array.from(root.querySelectorAll("[data-pq-step]")).map(el => {
      const d = el.dataset;
      if (d.type === "interstitial") {
        return {
          type: "interstitial", blockEl: el,
          stat: blank(d.stat), title: d.title || "", body: d.body || "",
          photo: blank(d.photo), icon: blank(d.icon), cta: blank(d.cta)
        };
      }
      const options = Array.from(el.querySelectorAll("[data-pq-option]")).map(o => {
        const od = o.dataset;
        return {
          v: blank(od.v) || od.label || "", label: od.label || "", sub: blank(od.sub),
          img: blank(od.img), why: blank(od.why), photo: blank(od.photo), tileLabel: blank(od.tileLabel)
        };
      });
      const step = {
        id: d.id || "", type: d.type || "single", multi: d.multi === "true", blockEl: el,
        q: blank(d.q), text: blank(d.text), sub: blank(d.sub), photo: blank(d.photo),
        footnote: blank(d.footnote), options
      };
      if (blank(d.checkLabel) || blank(d.checkText)) step.check = { label: d.checkLabel || "", text: d.checkText || "" };
      return step;
    });
  }

  /* ---------- One quiz instance per main section ---------- */
  function PilatesQuiz(root) {
    const C = JSON.parse(root.querySelector("[data-pq-config]").textContent);
    /* Textarea / comma settings arrive as plain strings */
    C.hero.ticks = splitList(C.hero.ticks);
    C.loadingSteps = splitList(C.loadingSteps);
    const R0 = C.planRules;
    R0.beginnerValues = splitCsv(R0.beginnerValues);
    R0.returningValues = splitCsv(R0.returningValues);
    R0.names = splitList(R0.names).reduce((m, line) => {
      const i = line.indexOf("|");
      if (i > 0) m[line.slice(0, i).trim()] = line.slice(i + 1).trim();
      return m;
    }, {});
    R0.defaultMinutes = String(R0.defaultMinutes || "15");
    C.sessionsPerWeek = String(C.sessionsPerWeek || "");
    C.offer.cycleDays = String(C.offer.cycleDays || "");
    const app = root.querySelector("[data-pq-app]");
    const sticky = root.querySelector("[data-pq-sticky]");
    const steps = readSteps(root);
    const realQs = steps.filter(s => s.type !== "interstitial").length;
    const state = { step: -1, answers: {}, view: "intro", plan: null };
    const brand = `<div class="pq-brand">${esc(C.brand)}<span>${esc(C.brandAccent)}</span></div>`;
    const L = C.labels;
    const OFFER = C.offer;
    const policyLinks = list => list.map(([t, u]) => `<a href="${esc(u)}" target="_blank" rel="noopener">${esc(t)}</a>`).join("");
    const OPENER = C.opener.enabled ? C.opener : null;

    /* ----- Quiz Lab entries (NyQuiz). Only active when this quiz has Quiz Lab keys. ----- */
    const T = C.tracking || {};
    const sdk = window.NyQuiz;
    const labOn = !isDesign && !!(T.quizKey && T.versionKey && sdk && typeof sdk.init === "function");
    const once = {};
    let lastScreen = null;
    if (labOn) {
      try {
        sdk.init({ quizKey: T.quizKey, versionKey: T.versionKey, endpoint: T.endpoint || "https://growth.nysonik.com", landingUrl: T.landingUrl || undefined });
      } catch (_) {}
    }
    function send(type, data) { if (!labOn) return; try { sdk.track(type, data || {}); } catch (_) {} }
    function sendOnce(type, data) { if (once[type]) return; once[type] = true; send(type, data); }
    function screen(key) {
      if (!labOn || lastScreen === key) return;
      sendOnce("quiz_start", { screenKey: key });
      send("screen_view", { screenKey: key, properties: lastScreen ? { previousScreenKey: lastScreen } : {} });
      lastScreen = key;
    }
    function stepKey(i) { const s = steps[i]; return s ? (s.id || ("info_" + (i + 1))) : "step_" + i; }
    function trackAnswer(s, value) {
      if (!labOn || !s || !s.id) return;
      const values = Array.isArray(value) ? value : [value];
      const labels = values.map(v => { const o = s.options.find(x => x.v === v); return o ? o.label : v; });
      send("answer", { screenKey: s.id, questionKey: s.id, answerValue: value, answerLabel: labels.join("; ") });
    }
    function flushLab(ms) {
      if (!labOn || typeof sdk.flush !== "function") return Promise.resolve();
      return Promise.race([Promise.resolve(sdk.flush()).catch(() => {}), new Promise(r => setTimeout(r, ms || 4000))]);
    }

    function priceCtx(p) {
      return {
        today_price: OFFER.todayPrice, renew_price: OFFER.renewPrice, cycle_days: OFFER.cycleDays,
        daily_price: OFFER.dailyPrice, sessions_per_week: C.sessionsPerWeek, count: realQs,
        minutes: p ? p.minutes : C.planRules.defaultMinutes,
        level: p ? p.level : "", plan_name: p ? p.name : C.planRules.defaultName
      };
    }

    function setOfferMode(on) {
      html.classList.toggle("pq-offer-active", on);
      sticky.hidden = !on;
    }

    /* ----- Intro / opener ----- */
    function renderOpener() {
      const o = OPENER;
      state.view = "intro"; setOfferMode(false);
      const q = steps.find(s => s.id === o.qid);
      if (!q) { C.opener.enabled = false; return renderHero(); }
      app.innerHTML = `<div class="pq-wrap pq-fade">
        <div class="pq-topbar">${brand}<span class="pq-small pq-muted">${esc(C.minutesLabel || "1-minute quiz")}</span></div>
        <div class="pq-opener">
          <p class="pq-eyebrow">${esc(o.eyebrow)}</p>
          <h1>${esc(o.title)}</h1>
          <p class="pq-opener-sub">${esc(o.sub)}</p>
          <div class="pq-tiles pq-cols-${q.options.length === 3 ? 3 : 2}" role="group" aria-label="${esc(o.prompt)}">
            ${q.options.map((op, i) => `<button class="pq-tile" type="button" data-v="${esc(op.v)}" id="pq-tile-${i}">
              <img src="${esc(op.photo || "")}" alt="" loading="eager">
              <span class="pq-tile-bar"><span>${esc(op.tileLabel || op.label)}</span><span aria-hidden="true">›</span></span>
            </button>`).join("")}
          </div>
          <p class="pq-small pq-muted">${esc(o.foot || "Tap to start. Your answers build your plan.")}</p>
          <div class="pq-policy">${policyLinks(OFFER.policies.slice(0, 3))}</div>
        </div>
      </div>`;
      screen("opener");
      app.querySelectorAll(".pq-tile").forEach(b => b.onclick = () => {
        state.answers[o.qid] = b.dataset.v;
        trackAnswer(q, b.dataset.v);
        state.step = steps.indexOf(q) + 1; renderStep(); toTop();
      });
    }

    function renderHero() {
      const H = C.hero;
      state.view = "intro"; setOfferMode(false);
      app.innerHTML = `<div class="pq-wrap pq-fade">
        <div class="pq-topbar">${brand}<span class="pq-small pq-muted">${esc(C.minutesLabel || "1-minute quiz")}</span></div>
        <div class="pq-hero">
          ${H.photo ? `<img class="pq-photo" src="${esc(H.photo)}" alt="${esc(H.alt || "")}">` : `<div class="pq-hero-art" aria-hidden="true">${HERO_ART}</div>`}
          <p class="pq-eyebrow">${esc(H.eyebrow)}</p>
          <h1>${esc(H.title)}</h1>
          <p class="pq-muted">${esc(H.sub)}</p>
          <ul class="pq-ticks">${H.ticks.map(t => `<li>${esc(t)}</li>`).join("")}</ul>
          <button class="pq-btn" type="button" id="pq-start">${esc(H.startLabel || "Take the quiz to find your plan")}</button>
          <p class="pq-small pq-muted">${esc(fill(L.questionsNote, priceCtx()))}</p>
        </div>
      </div>`;
      screen("intro");
      app.querySelector("#pq-start").onclick = () => { state.step = 0; renderStep(); toTop(); };
    }

    function renderIntro() {
      if (OPENER) return renderOpener();
      return renderHero();
    }

    /* ----- Questions ----- */
    function answeredCount(upto) {
      return steps.slice(0, upto).filter(s => s.type !== "interstitial").length;
    }

    function renderStep() {
      const s = steps[state.step];
      if (!s) return renderLoading();
      state.view = "quiz"; setOfferMode(false);
      screen(stepKey(state.step));
      /* Meta "Quiz Completed" sits on the result screen's CTA (the last tap before the offer page). */
      const doneAttr = "";
      const pct = Math.round((answeredCount(state.step) / realQs) * 100);
      const head = `<div class="pq-topbar"><button class="pq-backbtn" type="button" id="pq-back">${esc(L.back)}</button>${brand}<span class="pq-small pq-muted">${answeredCount(state.step) + (s.type === "interstitial" ? 0 : 1)} / ${realQs}</span></div>
        <div class="pq-progress" role="progressbar" aria-valuenow="${pct}" aria-valuemin="0" aria-valuemax="100"><i style="width:${pct}%"></i></div>`;

      if (s.type === "interstitial") {
        app.innerHTML = `<div class="pq-wrap pq-fade">${head}
          <div class="pq-inter">
            ${s.photo ? `<img class="pq-photo" src="${esc(s.photo)}" alt="">` : ""}
            ${s.icon && !s.photo ? `<div class="pq-inter-art">${ICONS[s.icon] || ""}</div>` : ""}
            ${s.stat ? `<div class="pq-big-stat">${esc(s.stat)}</div>` : ""}
            <h2>${esc(s.title)}</h2>
            <p class="pq-muted">${esc(s.body)}</p>
            <button class="pq-btn" type="button" id="pq-next"${doneAttr}>${esc(s.cta || L.continue)}</button>
          </div></div>`;
        app.querySelector("#pq-next").onclick = next;
      } else {
        const multi = s.type === "multi" || (s.type === "cards" && s.multi) || (s.type === "single" && s.multi);
        const cur = state.answers[s.id];
        const isOn = v => multi ? (cur || []).includes(v) : cur === v;
        let body;
        if (s.type === "cards") {
          body = `<div class="pq-cards ${s.options.length % 2 ? "pq-odd" : ""} ${s.options.some(o => o.photo) ? "pq-photo-cards" : ""}" role="group" aria-label="${esc(s.q || "")}">
            ${s.options.map((o, i) => `<button class="pq-card pq-opt-btn" type="button"${multi ? "" : doneAttr} data-v="${esc(o.v)}" aria-pressed="${isOn(o.v)}" id="pq-opt-${esc(s.id)}-${i}">
              ${o.photo ? `<img class="pq-card-photo" src="${esc(o.photo)}" alt="">` : (ICONS[o.img] || "")}<b>${esc(o.label)}</b>${o.sub ? `<small>${esc(o.sub)}</small>` : ""}</button>`).join("")}
          </div>`;
        } else if (s.type === "statement") {
          body = `<div class="pq-yesno" role="group" aria-label="${esc(s.text || "")}">
            ${s.options.map((o, i) => `<button class="pq-opt pq-opt-btn" type="button"${doneAttr} data-v="${esc(o.v)}" aria-pressed="${isOn(o.v)}" id="pq-opt-${esc(s.id)}-${i}">${esc(o.label)}</button>`).join("")}
          </div>`;
        } else {
          body = `<div class="pq-opts" role="group" aria-label="${esc(s.q || "")}">
            ${s.options.map((o, i) => `<button class="pq-opt pq-opt-btn ${multi ? "pq-multi" : ""}" type="button"${multi ? "" : doneAttr} data-v="${esc(o.v)}" aria-pressed="${isOn(o.v)}" id="pq-opt-${esc(s.id)}-${i}">
              <span class="pq-mark" aria-hidden="true"></span><span><b>${esc(o.label)}</b>${o.sub ? `<small>${esc(o.sub)}</small>` : ""}</span></button>`).join("")}
          </div>`;
        }
        app.innerHTML = `<div class="pq-wrap pq-fade">${head}
          ${s.photo ? `<img class="pq-photo pq-qphoto" src="${esc(s.photo)}" alt="">` : ""}
          <div class="pq-q">
            ${s.check ? `<div class="pq-check-card"><span class="pq-eyebrow">${esc(s.check.label)}</span><p>${esc(s.check.text)}</p></div>` : ""}
            ${s.type === "statement" ? `<p class="pq-eyebrow">${esc(s.q || L.statementEyebrow || "Do you relate to this?")}</p><div class="pq-stmt">${esc(s.text || "")}</div>` : `<h2>${esc(s.q || "")}</h2>`}
            ${s.sub ? `<p class="pq-muted">${esc(s.sub)}</p>` : ""}
          </div>
          ${body}
          ${multi ? `<div style="margin-top:18px"><button class="pq-btn" type="button" id="pq-next"${doneAttr} ${(cur || []).length ? "" : "disabled"}>${esc(L.continue)}</button><p class="pq-small pq-muted" style="margin-top:8px">${esc(L.pickAll)}</p></div>` : ""}
          ${s.footnote ? `<p class="pq-disclaimer" style="margin-top:18px">${esc(s.footnote)}</p>` : ""}
        </div>`;
        app.querySelectorAll(".pq-opt-btn").forEach(b => b.onclick = () => {
          const v = b.dataset.v;
          if (multi) {
            const arr = new Set(state.answers[s.id] || []);
            arr.has(v) ? arr.delete(v) : arr.add(v);
            state.answers[s.id] = [...arr];
            b.setAttribute("aria-pressed", arr.has(v));
            app.querySelector("#pq-next").disabled = arr.size === 0;
          } else {
            state.answers[s.id] = v;
            trackAnswer(s, v);
            app.querySelectorAll(".pq-opt-btn").forEach(x => x.setAttribute("aria-pressed", x === b));
            setTimeout(next, reduceMotion ? 0 : 220);
          }
        });
        if (multi) app.querySelector("#pq-next").onclick = () => { trackAnswer(s, state.answers[s.id] || []); next(); };
      }
      app.querySelector("#pq-back").onclick = () => {
        send("back", { screenKey: stepKey(state.step) });
        if (state.step === 0 || (OPENER && steps[state.step - 1] && steps[state.step - 1].id === OPENER.qid)) return renderIntro();
        state.step--; renderStep();
      };
    }

    function next() { state.step++; renderStep(); toTop(); }

    /* ----- Loading ----- */
    function renderLoading() {
      state.view = "loading"; setOfferMode(false);
      const items = C.loadingSteps.length ? C.loadingSteps : ["Matching your goal", "Setting your session length", "Building your 28-day plan"];
      app.innerHTML = `<div class="pq-wrap pq-fade"><div class="pq-topbar">${brand}</div>
        <div class="pq-loading">
          <svg class="pq-ring" viewBox="0 0 100 100" aria-hidden="true"><circle class="pq-ring-bg" cx="50" cy="50" r="42"/><circle class="pq-ring-fg" id="pq-ring" cx="50" cy="50" r="42"/></svg>
          <h2>${esc(L.loadingTitle)}</h2>
          <ul class="pq-steps-list">${items.map(t => `<li>${esc(t)}</li>`).join("")}</ul>
        </div></div>`;
      const lis = app.querySelectorAll(".pq-steps-list li");
      const total = reduceMotion ? 300 : 2400;
      requestAnimationFrame(() => { const r = app.querySelector("#pq-ring"); if (r) r.style.strokeDashoffset = 0; });
      lis.forEach((li, i) => setTimeout(() => li.classList.add("pq-done"), (total / lis.length) * (i + 0.6)));
      setTimeout(renderResult, total + 200);
    }

    /* ----- Plan logic ----- */
    function basePlan(a) {
      const R = C.planRules;
      const minutes = a[R.timeId] || R.defaultMinutes;
      const last = a[R.levelId];
      const level = R.beginnerValues.includes(last) ? R.beginnerLabel
        : (R.returningValues.includes(last) ? R.returningLabel : R.otherLabel);
      const f = a[R.focusId];
      const focus = (f && f.length) ? f : [R.defaultFocus];
      return { minutes, level, focus };
    }

    function buildPlan(a) {
      const R = C.planRules;
      const b = basePlan(a);
      const ctx = { minutes: b.minutes, level: b.level };
      const plan = {
        ...b,
        name: R.names[a[R.nameId]] || R.defaultName,
        tagline: fill(R.tagline, ctx),
        offerIntro: fill(R.offerIntro, ctx)
      };
      /* Optional "challenge dates" card on the result screen */
      const D = C.dates;
      if (D && D.enabled) {
        const start = new Date();
        const end = new Date(start); end.setDate(start.getDate() + (parseInt(D.span, 10) || 27));
        const picked = a[D.busyId];
        const busy = Array.isArray(picked) ? picked.length : (picked ? 1 : 0);
        const row = (label, date) => `<p style="display:flex;justify-content:space-between;gap:12px"><span>${esc(label)}</span><b>${fmtDate(date)}</b></p>`;
        plan.extra = `<span class="pq-eyebrow">${esc(D.label)}</span>${row(D.dayStart, start)}${row(D.dayEnd, end)}`
          + (busy && D.busyText ? `<p class="pq-small pq-muted">${esc(fill(D.busyText, { busy, weeks: busy > 1 ? "weeks" : "week" }))}</p>` : "")
          + (D.note ? `<p class="pq-small pq-muted">${esc(D.note)}</p>` : "");
      }
      return plan;
    }

    function chosenOptions() {
      const out = [];
      steps.forEach(s => {
        if (!s.options) return;
        const a = state.answers[s.id];
        const vals = Array.isArray(a) ? a : a ? [a] : [];
        vals.forEach(v => { const o = s.options.find(x => x.v === v); if (o) out.push(o); });
      });
      return out;
    }

    function getPlan() {
      const plan = buildPlan(state.answers);
      const why = chosenOptions().map(o => o.why).filter(Boolean);
      plan.why = [...new Set([...(plan.why || []), ...why])].slice(0, 4);
      return plan;
    }

    /* ----- Result ----- */
    function renderResult() {
      state.view = "result"; setOfferMode(false);
      const p = getPlan(); state.plan = p;
      const ctx = priceCtx(p);
      screen("result");
      if (!once.result_view) { once.result_view = true; send("result_view", { screenKey: "result", resultKey: R_KEY(p) }); }
      sendOnce("quiz_complete", { screenKey: "result" });
      app.innerHTML = `<div class="pq-wrap pq-fade">
        <div class="pq-topbar">${brand}<span class="pq-small pq-muted">${esc(L.result)}</span></div>
        <div class="pq-result-card">
          ${C.resultPhoto ? `<img class="pq-photo" src="${esc(C.resultPhoto)}" alt="">` : ""}
          <span class="pq-plan-badge">${esc(L.badge)}</span>
          <h1>${esc(p.name)}</h1>
          <p class="pq-muted">${esc(p.tagline)}</p>
          <div class="pq-facts">
            ${C.facts.map(f => `<div class="pq-fact"><b>${esc(fill(f[0], ctx))}</b><span>${esc(fill(f[1], ctx))}</span></div>`).join("")}
          </div>
          ${p.extra ? `<div class="pq-check-card">${p.extra}</div>` : ""}
          <div><h3 style="margin-bottom:10px">${esc(L.whyHeading)}</h3>
          <ul class="pq-why">${p.why.map(w => `<li><span>${esc(w)}</span></li>`).join("")}</ul></div>
          ${p.focus && p.focus.length ? `<div><p class="pq-small pq-muted" style="margin-bottom:6px">${esc(L.focusLabel)}</p><div class="pq-chips">${p.focus.map(f => `<span class="pq-chip">${esc(f)}</span>`).join("")}</div></div>` : ""}
          <button class="pq-btn" type="button" id="pq-to-offer" data-quiz-completed>${esc(L.resultCta)}</button>
        </div>
        <p class="pq-disclaimer" style="margin-top:16px">${esc(L.resultDisclaimer)}</p>
      </div>`;
      app.querySelector("#pq-to-offer").onclick = () => {
        send("cta_click", { screenKey: "result", properties: { action: "offer_summary" } });
        renderOffer(p); toTop();
      };
    }

    /* ----- Offer (the offer sections are real Shopify sections; we fill in the plan) ----- */
    function priceCard(id, ctx) {
      return `<div class="pq-price-card" id="${id}">
        <p class="pq-eyebrow">${esc(OFFER.cardEyebrow)}</p>
        <div class="pq-price-head"><span class="pq-price-big">${esc(OFFER.dailyPrice)}</span><span class="pq-price-terms">${esc(fill(OFFER.priceTerms, ctx))}</span></div>
        <p><b>${esc(fill(OFFER.priceSummary, ctx))}</b> ${esc(fill(OFFER.priceDetail, ctx))}</p>
        <button class="pq-btn pq-buy" type="button" data-annual-checkout>${esc(OFFER.buyLabel)}</button>
        <p class="pq-toast" hidden></p>
        <div class="pq-promise"><svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M12 2l8 3v6c0 5-3.4 9.4-8 11-4.6-1.6-8-6-8-11V5l8-3z" fill="none" stroke="currentColor" stroke-width="2"/><path d="M8 12l3 3 5-6" fill="none" stroke="currentColor" stroke-width="2"/></svg>
          <span><b>${esc(OFFER.promiseTitle)}</b> ${esc(OFFER.promiseText)}</span></div>
        <div class="pq-policy">${policyLinks(OFFER.policies)}</div>
      </div>`;
    }

    function hydrateOffer(p) {
      const ctx = priceCtx(p);
      document.querySelectorAll("[data-pq-brand]").forEach(el => { el.innerHTML = `${esc(C.brand)}<span>${esc(C.brandAccent)}</span>`; });
      document.querySelectorAll('[data-pq-bind="name"]').forEach(el => { el.textContent = p.name; });
      document.querySelectorAll('[data-pq-bind="intro"]').forEach(el => { el.textContent = p.offerIntro || p.tagline; });
      document.querySelectorAll('[data-pq-bind="why"]').forEach(el => {
        el.innerHTML = p.why.map(w => `<li><span>${esc(w)}</span></li>`).join("");
        el.hidden = !p.why.length;
      });
      document.querySelectorAll("[data-pq-tpl]").forEach(el => { el.textContent = fill(el.dataset.pqTpl, ctx); });
      document.querySelectorAll("[data-pq-price-card]").forEach(el => { el.innerHTML = priceCard(el.dataset.pqPriceCard, ctx); });
      document.querySelectorAll("[data-pq-note]").forEach(el => { el.hidden = !OFFER.showEditorNotes; });
      sticky.innerHTML = `<div class="pq-sticky-in"><p><b>${esc(fill(OFFER.stickyLine1, ctx))}</b><br>${esc(fill(OFFER.stickyLine2, ctx))}</p><button class="pq-btn pq-buy" type="button" data-annual-checkout>${esc(OFFER.buyLabel)}</button></div>`;
    }

    function R_KEY(p) { return String(p.name || "plan").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 80); }

    function renderOffer(p) {
      state.view = "offer";
      screen("offer");
      hydrateOffer(p);
      setOfferMode(true);
    }

    /* Buy buttons + "← My result" live in other sections, so listen on the document */
    function toast(buy, msg) {
      const t = buy.parentElement.querySelector(".pq-toast") || document.querySelector("#price-bottom .pq-toast");
      if (t) { t.hidden = !msg; t.textContent = msg || ""; if (msg) t.scrollIntoView({ block: "center", behavior: reduceMotion ? "auto" : "smooth" }); }
    }

    /* Cart attributes: a Quiz Lab visit when this quiz is registered (like 4-week-pilates),
       otherwise the landing-page source tags used by the other non-registered funnels. */
    async function cartAttributes() {
      if (labOn) {
        await flushLab(8000);
        const sessionId = typeof sdk.getSessionId === "function" ? sdk.getSessionId() : null;
        if (UUID_RE.test(sessionId || "")) {
          return [
            { key: "__nyq_session", value: sessionId },
            { key: "__nyq_quiz", value: T.quizKey },
            { key: "__nyq_version", value: T.versionKey }
          ];
        }
      }
      const attrs = [{ key: "__ny_lp", value: T.lpKey }, { key: "__ny_lp_version", value: T.lpVersion }].filter(a => a.value);
      const params = new URLSearchParams(window.location.search);
      UTM_KEYS.forEach(k => { const v = params.get(k); if (v) attrs.push({ key: k, value: v.slice(0, 200) }); });
      return attrs;
    }

    let checkoutPending = null;
    async function startCheckout(buy) {
      if (checkoutPending) return checkoutPending;
      const buttons = Array.from(document.querySelectorAll(".pq-buy"));
      buttons.forEach(b => { b.disabled = true; b.setAttribute("aria-busy", "true"); });
      toast(buy, OFFER.openingMsg);
      checkoutPending = (async () => {
        let url = null;
        if (C.checkoutMode === "cart") {
          try {
            const input = {
              lines: [{ quantity: 1, merchandiseId: ANNUAL_OFFER.variantId, sellingPlanId: ANNUAL_OFFER.sellingPlanId }],
              attributes: await cartAttributes()
            };
            const response = await fetch(ANNUAL_OFFER.endpoint, {
              method: "POST", credentials: "omit",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ query: ANNUAL_CART_MUTATION, variables: { input } }),
              signal: (window.AbortSignal && AbortSignal.timeout) ? AbortSignal.timeout(15000) : undefined
            });
            /* Never automatically retry a cart creation whose response was lost. */
            if (!response.ok) throw new Error("Checkout is unavailable. Please try again.");
            url = verifiedAnnualCheckout(await response.json(), input);
          } catch (err) {
            if (!OFFER.checkoutUrl) throw err;
            url = OFFER.checkoutUrl; /* fall back to the section's checkout link */
          }
        } else {
          url = OFFER.checkoutUrl;
        }
        if (!url) throw new Error(OFFER.missingCheckoutMsg);
        send("cta_click", { screenKey: "offer", properties: {
          action: "checkout_started", planLabel: "Flo Mat Pilates",
          amount: ANNUAL_OFFER.amount, currency: ANNUAL_OFFER.currency, billingPeriod: "28_days"
        } });
        await flushLab(4000);
        window.location.assign(trybeCheckoutUrl(url));
      })().catch(err => {
        toast(buy, err && err.message ? err.message : "Checkout is unavailable. Please try again.");
      }).finally(() => {
        checkoutPending = null;
        buttons.forEach(b => { b.disabled = false; b.removeAttribute("aria-busy"); });
      });
      return checkoutPending;
    }

    function onDocClick(e) {
      const buy = e.target.closest(".pq-buy");
      if (buy) {
        e.preventDefault();
        startCheckout(buy);
        return;
      }
      if (e.target.closest("[data-pq-back-result]")) { renderResult(); toTop(); }
    }
    document.addEventListener("click", onDocClick);

    /* ----- Theme editor: jump to the step whose block is selected ----- */
    function onBlockSelect(e) {
      const el = e.target && e.target.closest && e.target.closest("[data-pq-step]");
      if (!el || !root.contains(el)) return;
      const i = steps.findIndex(s => s.blockEl === el);
      if (i < 0) return;
      if (OPENER && steps[i].id === OPENER.qid) { renderIntro(); return; }
      state.step = i; renderStep();
    }
    if (isDesign) document.addEventListener("shopify:block:select", onBlockSelect);

    /* ----- Start ----- */
    if (isDesign) {
      html.classList.add("pq-design");
      const preview = getPlan();
      hydrateOffer(preview);
      sticky.hidden = true;
    }
    renderIntro();

    return {
      root,
      rehydrate() { if (isDesign) { hydrateOffer(state.plan || getPlan()); sticky.hidden = true; } else if (state.plan && state.view === "offer") hydrateOffer(state.plan); },
      destroy() {
        document.removeEventListener("click", onDocClick);
        document.removeEventListener("shopify:block:select", onBlockSelect);
        html.classList.remove("pq-offer-active");
      }
    };
  }

  /* ---------- Boot + theme editor lifecycle ---------- */
  let instance = null;
  function boot() {
    const root = document.querySelector("[data-pq-root]");
    if (instance) { instance.destroy(); instance = null; }
    if (root) instance = PilatesQuiz(root);
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();

  document.addEventListener("shopify:section:load", e => {
    if (e.target.querySelector("[data-pq-root]")) boot();
    else if (instance && e.target.querySelector(".pq-offer")) instance.rehydrate();
  });
  document.addEventListener("shopify:section:unload", e => {
    if (instance && e.target.contains(instance.root)) { instance.destroy(); instance = null; }
  });
})();
